import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';

/**
 *  ÉRTEKEZLET  ·  online megbeszélés és a felvételek visszanézése
 *
 *  A konferencia-kiszolgáló címe az intézmény törzsadatából jön (`feladatellatas.cooperante`),
 *  és KÉTFÉLE alakot ismerünk:
 *
 *    - sablon:  https://ertekezlet.pelda.hu/{szoba}   → a {szoba} helyére kerül a szobakód
 *    - régi:    https://cooperante.pelda.hu/          → a kód `?roomId=` paraméterként megy
 *
 *  A címet NEM a belépéskor eltárolt adatból vesszük, hanem a kiszolgálótól kérdezzük:
 *  így egy frissen beírt cím azonnal él, nem csak ki- és belépés után.
 *
 *  A beágyazás a Jitsi hivatalos kapcsolatán megy, ha az elérhető: így átadjuk a belépett
 *  kolléga nevét, és az értekezlet végét (kilépés vagy „mindenkinek vége”) meg is tudjuk –
 *  ilyenkor magunktól visszatérünk erre a képernyőre. Ha az nem tölthető be (régi, nem
 *  Jitsi kiszolgáló), marad a sima beágyazás.
 */
@Component({
  selector: 'app-ertekezlet',
  templateUrl: './ertekezlet.component.html',
  styleUrls: ['./ertekezlet.component.scss'],
})
export class ErtekezletComponent implements OnInit, OnDestroy {

  sajatIntezmeny: any;
  userMetadata: any;

  /** Épp fut-e értekezlet (ilyenkor az ablak kapja a teljes képernyőt). */
  folyamatban = false;
  szobakod = '';

  felvetelek: any[] = [];
  felvetelekLathato = false;
  felvetelekToltes = false;
  jatszott: any = null;
  lejatszoCim: string | null = null;

  private api: any = null;
  private cimKiszolgalorol: string | null = null;

  constructor(private ref: ChangeDetectorRef, private data: DataService, private http: HttpClient,
              private router: Router) { }

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.userMetadata = await this.data.getFromStorage('userMetadata');
    await this.beallitasFrissites();
    await this.felvetelekBetoltese();
  }

  ngOnDestroy() {
    try { this.api?.dispose?.(); } catch (e) { /* mindegy */ }
  }

  /* ── Beállítás ─────────────────────────────────────────────────────────── */

  get cim(): string {
    return String(this.cimKiszolgalorol ?? this.sajatIntezmeny?.cooperante ?? '').trim();
  }

  get beallitva(): boolean {
    return !!this.cim;
  }

  get intezmenyNev(): string {
    return String(this.sajatIntezmeny?.intezmenyName || this.sajatIntezmeny?.name || '').trim();
  }

  private get intezmenyUt(): string {
    return String(this.sajatIntezmeny?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
  }

  private async beallitasFrissites() {
    if (!this.intezmenyUt) return;
    try {
      const v: any = await firstValueFrom(this.http.get(`/api/ertekezlet/beallitas/${this.intezmenyUt}`));
      this.cimKiszolgalorol = String(v?.cim || '').trim() || null;
      this.ref.detectChanges();
    } catch (e) { /* marad a belépéskor tárolt érték */ }
  }

  /* ── Értekezlet indítása és elhagyása ──────────────────────────────────── */

  /** Nyolc karakteres szobakód – ennyit vár a csatlakozás-mező is. */
  private ujSzobakod(): string {
    const betuk = 'abcdefghijkmnopqrstuvwxyz23456789';
    let k = '';
    for (let i = 0; i < 8; i++) k += betuk[Math.floor(Math.random() * betuk.length)];
    return k;
  }

  private szobaCim(szoba: string): string {
    const alap = this.cim;
    if (alap.includes('{szoba}')) return alap.replace('{szoba}', encodeURIComponent(szoba));
    return `${alap}?roomId=${encodeURIComponent(szoba)}`;
  }

  private get jitsiE(): boolean {
    return this.cim.includes('{szoba}');
  }

  private async apiBetoltes(origin: string): Promise<any> {
    if ((window as any).JitsiMeetExternalAPI) return (window as any).JitsiMeetExternalAPI;
    await new Promise<void>((ok, hiba) => {
      const s = document.createElement('script');
      s.src = `${origin}/external_api.js`;
      s.onload = () => ok();
      s.onerror = () => hiba(new Error('nem tölthető be'));
      document.head.appendChild(s);
    });
    return (window as any).JitsiMeetExternalAPI;
  }

  /** A belépett kolléga neve – így nem kell beírnia a belépő ablakban. */
  private sajatNev(): string {
    const m = this.userMetadata || {};
    return String(m.full_name || [m.viselt_vez, m.viselt_ker].filter(Boolean).join(' ') || '').trim();
  }

  async newMeeting(id: string | null = null) {
    if (!this.beallitva) {
      this.data.sendToast('Nincs beállítva értekezlet-kiszolgáló. Az adminisztrátor a Törzsadatok → feladatellatas „cooperante” mezőjében adhatja meg a címet.', 'warning');
      return;
    }
    const szoba = id || this.ujSzobakod();
    this.folyamatban = true;
    this.ref.detectChanges();

    /*  BEJELENTJÜK, MELYIK ISKOLA SZOBÁJA. Egy konferencia-kiszolgálót több intézmény is
     *  használhat, a felvétel fájlneve viszont csak a szoba nevét hordozza – e nélkül a kész
     *  felvétel rossz iskola listájába kerülne. Ha nem megy át, az értekezlet ettől még indul. */
    try {
      await firstValueFrom(this.http.post(`/api/ertekezlet/szoba/${this.intezmenyUt}`, { szoba }));
    } catch (e) {
      console.warn('Értekezlet: a szoba bejelentése nem sikerült.', e);
    }

    if (this.jitsiE) {
      try {
        const origin = new URL(this.cim.replace('{szoba}', 'x')).origin;
        const Api = await this.apiBetoltes(origin);
        const keret = document.getElementById('frame');
        if (keret) keret.setAttribute('src', '');
        this.api?.dispose?.();
        this.api = new Api(new URL(origin).host, {
          roomName: szoba,
          parentNode: document.getElementById('ertekezlet'),
          width: '100%', height: '100%',
          userInfo: { displayName: this.sajatNev() },
          configOverwrite: { prejoinPageEnabled: false },
        });
        this.api.addListener('readyToClose', () => this.exit());
        return;
      } catch (e) {
        console.warn('Értekezlet: a hivatalos kapcsolat nem tölthető be, sima beágyazás lesz.', e);
      }
    }
    document.getElementById('frame')?.setAttribute('src', this.szobaCim(szoba));
  }

  connectMeeting() {
    let k = String(this.szobakod || '').trim();
    if (k.includes('http')) k = k.replace(/[?#].*$/, '').replace(/\/+$/, '').split('/').pop() || '';
    if (k.length < 4) {
      this.data.sendToast('Nem megfelelő szobakód. Írd be a kapott kódot, vagy illeszd be a teljes linket.', 'warning');
      return;
    }
    this.newMeeting(k);
  }

  exit() {
    try { this.api?.dispose?.(); } catch (e) { /* mindegy */ }
    this.api = null;
    document.getElementById('frame')?.setAttribute('src', '');
    this.folyamatban = false;
    this.szobakod = '';
    this.ref.detectChanges();
    //  A most befejezett értekezlet felvétele pár másodperccel később ér a fájltárba.
    setTimeout(() => this.felvetelekBetoltese(), 8000);
  }

  /* ── Felvételek ────────────────────────────────────────────────────────── */

  async felvetelekBetoltese() {
    if (!this.intezmenyUt) return;
    this.felvetelekToltes = true;
    try {
      const v: any = await firstValueFrom(this.http.get(`/api/ertekezlet/felvetelek/${this.intezmenyUt}`));
      this.felvetelek = v?.felvetelek || [];
      this.felvetelekLathato = true;
    } catch (e) {
      //  403: nincs joga a felvételekhez – ilyenkor a lista meg se jelenjen.
      this.felvetelekLathato = false;
      this.felvetelek = [];
    } finally {
      this.felvetelekToltes = false;
      this.ref.detectChanges();
    }
  }

  /*  A fájl a TOKENES megnyitóval nyílik. A linkes megnyitás nem viszi a belépési jegyet,
   *  a kiszolgáló pedig – helyesen – nem ad ki fájlt kilét nélkül. */
  fajlNyit(url: string, nev?: string) { this.data.megnyitFajl(url, nev || 'Felvétel'); }

  felvetelCime(f: any): string {
    return `/api/ertekezlet/felvetel/${f.id}/${this.intezmenyUt}`;
  }

  /*  A `<video src>` nem visz tokent, ezért a lejátszás előtt tokennel kérünk egy erre a
   *  felvételre szóló, rövid életű jegyet, és az kerül a videó címébe. */
  async jatszd(f: any) {
    this.lejatszoCim = null;
    this.jatszott = this.jatszott?.id === f.id ? null : f;
    this.ref.detectChanges();
    if (!this.jatszott) return;
    try {
      const v: any = await firstValueFrom(this.http.get(`/api/ertekezlet/jegy/${f.id}/${this.intezmenyUt}`));
      if (this.jatszott?.id !== f.id) return;
      this.lejatszoCim = `${this.felvetelCime(f)}?jegy=${encodeURIComponent(v?.jegy || '')}`;
    } catch (e: any) {
      this.jatszott = null;
      this.data.sendToast(e?.error?.error || 'A felvétel nem játszható le.', 'error');
    }
    this.ref.detectChanges();
  }

  meret(b: number): string {
    const mb = (Number(b) || 0) / 1024 / 1024;
    return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
  }

  hossz(mp: number): string {
    const s = Number(mp) || 0;
    const p = Math.floor(s / 60);
    const m = s % 60;
    return p ? `${p} perc ${m} mp` : `${m} mp`;
  }

  /**
   *  MEGOSZTÁS ÜZENETBEN.
   *
   *  A felvételt eddig csak az láthatta, aki ezt a képernyőt is megnyithatja –
   *  adminisztrátor. Egy szülői értekezletnek viszont épp az lenne az értelme, hogy
   *  aki nem tudott ott lenni, utólag megnézhesse.
   *
   *  A fájl NEM megy sehova: az üzenetkezelő egy hivatkozást tesz a levélre (egy órás
   *  felvétel több száz megabájt), és a címzettek ugyanazt az egy fájlt nézik vissza,
   *  itt a rendszerben. Kifelé mutató, bárki által megnyitható link nem keletkezik.
   */
  megosztasUzenetben(f: any) {
    this.router.navigate(['/uzenetek'], {
      state: { felvetel: { id: f.id, szoba: f.szoba, kezdete: f.kezdete, feltoltve: f.feltoltve,
                           meret: f.meret, hossz_mp: f.hossz_mp } },
    });
  }

  async felvetelTorles(f: any) {
    /*  A megosztott felvétel törlése a MÁR ELKÜLDÖTT leveleket is üressé teszi: a
     *  csatolmány hivatkozás, nem másolat. Ezt ki kell mondani, mielőtt törlünk. */
    if (!confirm(`Biztosan törlöd ezt a felvételt? (${f.szoba})\n\n`
      + 'Ha megosztottad üzenetben, a címzettek ezután már nem tudják visszanézni: '
      + 'a levélben hivatkozás van, nem másolat.')) return;
    try {
      await firstValueFrom(this.http.delete(`/api/ertekezlet/felvetel/${f.id}/${this.intezmenyUt}`));
      this.data.sendToast('A felvétel törölve.', 'success');
      if (this.jatszott?.id === f.id) this.jatszott = null;
      await this.felvetelekBetoltese();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A felvétel törlése nem sikerült.', 'error');
    }
  }
}
